import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { useResponsive } from '@garnish/core';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useIsFoldableDevice } from '../../hooks';
import { useRedirectAfterAuthStateChange } from '../../navigation';
import { JoinOrSignInScreenContentEmbeddedFrameFlow } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const JoinOrSignInScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();
  const { redirect } = useLocalSearchParams<{ redirect?: string }>();
  const { currentBreakpoint } = useResponsive();
  const { top: safeAreaTop } = useSafeAreaInsets();
  const isFoldableDevice = useIsFoldableDevice();

  // ─── Refs ────────────────────────────────────────────────────────────

  const isLoggedInOnMountRef = useRef(isLoggedIn);

  // ─── Derived ─────────────────────────────────────────────────────────

  // iOS presents this screen as a sheet below the status bar, except on foldables.
  const isFullScreen = isFoldableDevice || Platform.OS !== 'ios';
  const shouldApplyTopSafeArea = currentBreakpoint.isXS && isFullScreen;

  // ─── Handlers ────────────────────────────────────────────────────────

  /**
   * Redirect customers who open this screen while already signed in.
   *
   * NOTE: Sign-ins that happen on this screen are redirected by `usePostLoginRedirect`.
   */
  const redirectSignedInCustomer = useCallback(() => {
    if (!isLoggedInOnMountRef.current) {
      return;
    }

    redirectAfterAuthStateChange(redirect);
  }, [redirect, redirectAfterAuthStateChange]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(redirectSignedInCustomer, [redirectSignedInCustomer]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => (shouldApplyTopSafeArea ? { paddingTop: safeAreaTop } : undefined),
    [safeAreaTop, shouldApplyTopSafeArea],
  );
  const containerStyles = [styles.container, containerDynamicStyles];

  // ─────────────────────────────────────────────────────────────────────

  if (isLoggedIn) {
    return null;
  }

  return (
    <View style={containerStyles}>
      <JoinOrSignInScreenContentEmbeddedFrameFlow />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
